import type {User} from '@reados/contracts';

export type AuthBootstrapState={phase:'checking'|'recovering'|'unavailable'|'ready';busy:boolean};
type Clock={now:()=>number;schedule:(callback:()=>void,delay:number)=>unknown;cancel:(handle:unknown)=>void};
type Options={read:(signal:AbortSignal)=>Promise<unknown>;onUser:(user:User|null)=>void;timeoutMs?:number;retryDelays?:readonly number[];clock?:Clock};
const browserClock:Clock={now:()=>Date.now(),schedule:(callback,delay)=>setTimeout(callback,delay),cancel:handle=>clearTimeout(handle as ReturnType<typeof setTimeout>)};

// A failed or malformed read cannot establish that the browser is signed out.
export function confirmedSessionUser(value:unknown):User|null{
 if(!value||typeof value!=='object'||!Object.hasOwn(value,'user'))throw new Error('Invalid session response');
 const user=(value as {user:unknown}).user;
 if(user===null)return null;
 if(!user||typeof user!=='object')throw new Error('Invalid session user');
 const candidate=user as Partial<User>;
 if(typeof candidate.id!=='string'||!candidate.id.trim()||typeof candidate.email!=='string'||typeof candidate.name!=='string'||candidate.role!==undefined&&!['user','admin','super_admin'].includes(candidate.role))throw new Error('Invalid session user');
 return candidate as User;
}

export function createAuthBootstrap(options:Options){
 const clock=options.clock??browserClock,retryDelays=options.retryDelays??[1000,3000],timeoutMs=options.timeoutMs??10_000;
 let state:AuthBootstrapState={phase:'checking',busy:true},active=false,generation=0,attempts=0,failed=false,lastStarted=-Infinity;
 let request:{generation:number;controller:AbortController;timeout:unknown}|null=null,retryTimer:unknown=null;
 const listeners=new Set<()=>void>();
 const publish=(next:AuthBootstrapState)=>{state=next;for(const listener of listeners)listener();};
 const clearRetry=()=>{if(retryTimer!==null){clock.cancel(retryTimer);retryTimer=null;}};
 const invalidate=()=>{generation++;clearRetry();if(request){clock.cancel(request.timeout);request.controller.abort();request=null;}};
 const accept=(user:User|null)=>{invalidate();options.onUser(user);publish({phase:'ready',busy:false});};
 function run(){
  if(!active||request||state.phase==='ready')return;
  clearRetry();attempts++;lastStarted=clock.now();
  const current={generation:++generation,controller:new AbortController(),timeout:null as unknown};request=current;
  const isCurrent=()=>active&&request===current&&generation===current.generation;
  const fail=()=>{
   if(!isCurrent())return;
   clock.cancel(current.timeout);request=null;current.controller.abort();failed=true;
   const delay=retryDelays[attempts-1];
   publish({phase:delay===undefined?'unavailable':'recovering',busy:false});
   if(delay!==undefined)retryTimer=clock.schedule(()=>{retryTimer=null;run();},delay);
  };
  publish({phase:failed?'recovering':'checking',busy:true});
  current.timeout=clock.schedule(fail,timeoutMs);
  try{
   void options.read(current.controller.signal).then(value=>{
    if(!isCurrent())return;
    let user:User|null;try{user=confirmedSessionUser(value);}catch{fail();return;}
    accept(user);
   },fail);
  }catch{fail();}
 }
 const retry=()=>{if(!active||request||state.phase==='ready')return;attempts=0;run();};
 return {
  getSnapshot:()=>state,
  subscribe:(listener:()=>void)=>{listeners.add(listener);return()=>{listeners.delete(listener);};},
  start:()=>{if(active)return;active=true;if(state.phase!=='ready'){attempts=0;run();}},
  stop:()=>{active=false;invalidate();},
  retry,
  resume:()=>{
   // Deduplicate mobile focus/online bursts without extending a pending retry budget.
   if(!active||request||state.phase==='ready'||clock.now()-lastStarted<1000)return;
   if(retryTimer!==null)run();else retry();
  },
  setUser:(user:User|null)=>{if(active)accept(user);},
 };
}
